import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { twMerge } from 'tailwind-merge';
import { BrokerCode } from '@type/brokerType';
import { useMobxInstance } from '@client/clientUtils/mobxUtil';
import BirthYearInput from './BirthYearInput';
import BrokerSelection from './BrokerSelection';
import { CheckIcon } from 'lucide-react';
import modalStore from '@client/stores/modalStore';

export type UserInfoSetupStep = 1 | 2;

class ViewStore {
    step: UserInfoSetupStep = 1;
    selectedBirthYear: number | null = null;
    selectedBrokers: BrokerCode[] = [];

    constructor() {
        makeAutoObservable(this);
    }

    /**
     * 단계 변경
     */
    setStep = (step: UserInfoSetupStep) => {
        this.step = step;
    };

    /**
     * 다음 단계로 이동
     */
    nextStep = () => {
        if (this.step === 1) {
            this.step = 2;
        }
    };

    /**
     * 이전 단계로 이동
     */
    prevStep = () => {
        if (this.step === 2) {
            this.step = 1;
        }
    };

    /**
     * 생년 설정
     */
    setBirthYear = (year: number) => {
        this.selectedBirthYear = year;
    };

    /**
     * 증권사 선택 목록 설정
     */
    setBrokers = (brokers: BrokerCode[]) => {
        this.selectedBrokers = brokers;
    };

    /**
     * 1단계 완료 가능 여부
     */
    get canCompleteStep1(): boolean {
        return this.selectedBirthYear !== null;
    }

    /**
     * 2단계 완료 가능 여부 (최소 1개 증권사 선택)
     */
    get canCompleteStep2(): boolean {
        return this.selectedBrokers.length > 0;
    }

    /**
     * 전체 완료 가능 여부
     */
    get canComplete(): boolean {
        return this.canCompleteStep1 && this.canCompleteStep2;
    }

    /**
     * 상태 초기화
     */
    reset = () => {
        this.step = 1;
        this.selectedBirthYear = null;
        this.selectedBrokers = [];
    };
}

const UserInfoSetupModal = observer(({ modalId }: { modalId: string }) => {
    const viewStore = useMobxInstance(ViewStore);

    const handleNext = () => {
        if (viewStore.step === 1 && viewStore.canCompleteStep1) {
            viewStore.nextStep();
        }
    };

    const handleComplete = () => {
        if (viewStore.canComplete) {
            modalStore.setReturnValue(modalId, {
                birthYear: viewStore.selectedBirthYear!,
                brokers: Array.from(viewStore.selectedBrokers),
            });

            modalStore.close(modalId);
        }
    };

    return (
        <div className="flex flex-col items-center gap-7">
            {/* 진행 단계 표시 */}
            <ul className="steps steps-sm">
                <li className={twMerge('step', viewStore.step >= 1 && 'step-primary')}>
                    <span className="step-icon">
                        <CheckIcon className="size-4" />
                    </span>
                </li>
                <li className={twMerge('step', viewStore.step >= 2 && 'step-primary')}>
                    <span className="step-icon">
                        <CheckIcon className="size-4" />
                    </span>
                </li>
            </ul>
            {/* 컨텐츠 */}
            {viewStore.step === 1 && (
                <div className="flex w-full flex-col items-center gap-6">
                    <div className="text-center">
                        <h2 className="mb-2 text-xl font-semibold tracking-tight">
                            태어난 해를 선택해주세요
                        </h2>
                        <p className="text-base-content/70 text-sm">
                            연령별 통계 제공에 사용됩니다
                        </p>
                    </div>
                    <div className="mt-2 w-full">
                        <BirthYearInput
                            value={viewStore.selectedBirthYear}
                            onChange={viewStore.setBirthYear}
                            className="mx-auto max-w-sm"
                        />
                    </div>
                </div>
            )}
            {viewStore.step === 2 && (
                <div className="flex w-full flex-col items-center gap-6">
                    <div className="text-center">
                        <h2 className="mb-2 text-xl font-semibold tracking-tight">
                            자주 사용하는 증권사를 선택해주세요
                        </h2>
                        <p className="text-base-content/70 text-sm">여러 개를 선택할 수 있습니다</p>
                    </div>
                    <div className="max-h-96 w-full overflow-y-auto px-4">
                        <BrokerSelection
                            selectedCodes={viewStore.selectedBrokers}
                            onSelectionChange={viewStore.setBrokers}
                        />
                    </div>
                </div>
            )}
            <div className="flex w-full justify-between">
                {viewStore.step === 1 ? (
                    <button
                        type="button"
                        className="btn btn-lg btn-soft btn-primary btn-block"
                        onClick={handleNext}
                        disabled={!viewStore.canCompleteStep1}
                    >
                        다음
                    </button>
                ) : (
                    <button
                        type="button"
                        className="btn btn-lg btn-soft btn-primary btn-block"
                        onClick={handleComplete}
                        disabled={!viewStore.canCompleteStep2}
                    >
                        완료
                    </button>
                )}
            </div>
        </div>
    );
});

export default UserInfoSetupModal;
